/* eslint-disable react-refresh/only-export-components -- arquivo mistura componente com hooks/types/helpers; refactor pra split é cosmético (HMR fast-refresh). */
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'

export type Theme = 'dark' | 'dim' | 'light'
export const THEMES: Theme[] = ['dark', 'dim', 'light']

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
  cycleTheme: () => void
  /** Legacy — toggles between dark and light, ignores dim. */
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const STORAGE_KEY = 'runna_theme'

function initialTheme(): Theme {
  const saved = localStorage.getItem(STORAGE_KEY)
  if (saved === 'dark' || saved === 'dim' || saved === 'light') return saved
  // Respect OS preference on first visit — light only if explicitly set.
  if (window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light'
  return 'dark'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(initialTheme)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  const setTheme = (next: Theme) => setThemeState(next)
  const cycleTheme = () => setThemeState(prev => {
    const idx = THEMES.indexOf(prev)
    return THEMES[(idx + 1) % THEMES.length]
  })
  const toggleTheme = () => setThemeState(prev => (prev === 'light' ? 'dark' : 'light'))

  return (
    <ThemeContext.Provider value={{ theme, setTheme, cycleTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used within ThemeProvider')
  return context
}
